import { useEffect, useRef, useState } from 'react';

import Button from '../components/Button';
import { t } from '../i18n/langue';

/**
 * Écran de diagnostic de la coque — hérité du lot 0, **hors registre**.
 *
 * Il ne porte aucun écran produit. Son métier est de prouver que les cinq étages
 * du lot 0 tiennent réellement sur un iPhone : manifeste accepté, ouverture en
 * standalone, service worker enregistré, zone sûre conforme.
 *
 * Il était la coque ; #75 lui a donné une adresse à lui, `/diagnostic`, et l'a
 * sorti du registre de `mobileScreens.ts`. Deux raisons : au registre il
 * compterait dans les seuils 0/1/≥2 et le comportement « un seul écran » ne
 * serait plus testable sur un vrai téléphone ; et un panneau de navigation n'a
 * pas à porter d'outil de mise au point. Il reste joignable en tapant l'URL —
 * c'est la seule surface qui dit la vérité sur l'appareil jusqu'à ce que #80
 * livre « État des notifications », son successeur officiel.
 *
 * **Il est monté avant tout le reste** (cf. `Shell.tsx`) : ni porte standalone,
 * ni fournisseurs, ni routeur. Un diagnostic qui dépend de ce qu'il diagnostique
 * ne prouve rien.
 *
 * Le sas hors standalone qu'il portait vit désormais dans `shell/SasInstallation.tsx`.
 */

/**
 * L'API Notification n'existe pas dans un onglet iOS : seule une web app
 * installée y a droit. On teste `requestPermission` et pas seulement la
 * présence du constructeur — c'est le point que #38 doit relever.
 */
function notificationDisponible(): boolean {
    return 'Notification' in window && typeof Notification.requestPermission === 'function';
}

/**
 * Rend l'enregistrement du service worker, en tolérant qu'il soit encore EN VOL.
 *
 * `main.tsx` diffère `register()` à l'événement `load` — qui attend toutes les
 * sous-ressources — alors que ce composant mesure au montage de React. Or
 * `getRegistration()` rend `undefined` pour un enregistrement **en cours**, pas
 * seulement pour un enregistrement absent : au premier lancement suivant une
 * installation, la coque annonçait donc « aucun » sur un SW parfaitement sain
 * (mesuré sur iPhone, iOS 26.5.2 — #72). Android gagnait la course et masquait
 * le défaut.
 *
 * L'enjeu déborde cette coque : l'écran « État des notifications » (#21) porte
 * le même point « ce téléphone est enregistré », et le lirait de la même façon.
 *
 * `ready` attend l'activation, mais ne se résout **jamais** si l'enregistrement
 * échoue — d'où la borne, sans laquelle un vrai échec resterait affiché « en
 * cours » indéfiniment, c'est-à-dire un mensonge par omission sur l'écran dont
 * tout le métier est de ne pas mentir.
 */
async function attendreEnregistrement(
    delaiMs = 10_000,
): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) return null;

    // Chemin chaud : déjà enregistré (réouverture), aucune attente.
    const dejaLa = await navigator.serviceWorker.getRegistration();
    if (dejaLa) return dejaLa;

    // Chemin froid : premier lancement après installation.
    return Promise.race([
        navigator.serviceWorker.ready,
        new Promise<null>((resoudre) => {
            window.setTimeout(() => resoudre(null), delaiMs);
        }),
    ]);
}

type EtatServiceWorker =
    | { etat: 'attente' }
    | { etat: 'enregistre'; scope: string }
    | { etat: 'absent' };

interface Releve {
    libelle: string;
    valeur: string;
    /** Omis quand le relevé est informatif : tout verdict serait dépendant de la plateforme. */
    conforme?: boolean;
    note?: string;
}

export default function Diagnostic() {
    const sondeRef = useRef<HTMLDivElement>(null);
    const [standalone, setStandalone] = useState(false);
    const [releves, setReleves] = useState<Releve[]>([]);
    const [permission, setPermission] = useState<NotificationPermission | 'indisponible'>(() =>
        notificationDisponible() ? Notification.permission : 'indisponible',
    );
    // Mesuré à part des autres relevés : il est le seul à pouvoir attendre, et
    // faire patienter toute la table sur lui la rendrait vide pendant ce temps.
    const [serviceWorker, setServiceWorker] = useState<EtatServiceWorker>({ etat: 'attente' });

    const demanderPermission = async () => {
        if (!notificationDisponible()) return;
        try {
            setPermission(await Notification.requestPermission());
        } catch {
            // Les WebKit anciens n'exposent que la forme à callback : on relit
            // l'état plutôt que de laisser l'écran mentir sur un rejet de promesse.
            setPermission(Notification.permission);
        }
    };

    useEffect(() => {
        const requete = window.matchMedia('(display-mode: standalone)');
        setStandalone(requete.matches);

        const surChangement = (e: MediaQueryListEvent) => setStandalone(e.matches);
        requete.addEventListener('change', surChangement);
        return () => requete.removeEventListener('change', surChangement);
    }, []);

    useEffect(() => {
        let annule = false;

        void attendreEnregistrement().then((enregistrement) => {
            if (annule) return;
            setServiceWorker(
                enregistrement
                    ? { etat: 'enregistre', scope: enregistrement.scope }
                    : { etat: 'absent' },
            );
        });

        return () => {
            annule = true;
        };
    }, []);

    useEffect(() => {
        let annule = false;

        const mesurer = async () => {
            // La zone sûre se lit sur un élément réel : une propriété personnalisée
            // rendrait le jeton env(…) brut au lieu de sa valeur calculée.
            const sonde = sondeRef.current;
            const haut = sonde ? window.getComputedStyle(sonde).paddingTop : '0px';
            const bas = sonde ? window.getComputedStyle(sonde).paddingBottom : '0px';
            const zoneSure = `${haut} / ${bas}`;

            if (annule) return;

            setReleves([
                {
                    libelle: t('diagnostic.ouverture'),
                    valeur: standalone ? 'standalone' : t('diagnostic.ongletNavigateur'),
                    conforme: standalone,
                },
                {
                    libelle: t('diagnostic.serviceWorker'),
                    valeur:
                        serviceWorker.etat === 'enregistre'
                            ? serviceWorker.scope
                            : serviceWorker.etat === 'attente'
                              ? t('diagnostic.enregistrementEnCours')
                              : t('diagnostic.aucun'),
                    // Pas de verdict tant que l'enregistrement est en vol : un
                    // rouge transitoire au premier lancement se lirait comme une
                    // panne (#72).
                    conforme:
                        serviceWorker.etat === 'attente'
                            ? undefined
                            : serviceWorker.etat === 'enregistre',
                },
                {
                    libelle: t('diagnostic.pushManager'),
                    valeur: 'PushManager' in window ? t('diagnostic.oui') : t('diagnostic.non'),
                    conforme: 'PushManager' in window,
                },
                {
                    libelle: t('diagnostic.apiNotification'),
                    valeur: notificationDisponible() ? t('diagnostic.oui') : t('diagnostic.non'),
                    conforme: notificationDisponible(),
                },
                {
                    libelle: t('diagnostic.permission'),
                    valeur: permission,
                    // AUCUN verdict : c'est la valeur mesurée, pas une valeur attendue.
                    // Le bug WebKit 320551 prétend justement qu'elle ment — rendre
                    // « default » en rouge masquerait ce que ce relevé vient mesurer.
                    note: t('diagnostic.permissionNote'),
                },
                {
                    libelle: t('diagnostic.zoneSure'),
                    valeur: zoneSure,
                    // AUCUN verdict ici : la valeur attendue dépend de la plateforme,
                    // et les DEUX insets ne disent pas la même chose.
                    //
                    // BAS — c'est lui qui atteste viewport-fit=cover (#34). Mesuré à
                    // 34px sur iPhone Face ID, soit l'indicateur d'accueil au pixel
                    // près ; sans `cover` il vaudrait 0 lui aussi.
                    //
                    // HAUT — 0px est NORMAL tant qu'on ne déclare pas
                    // `apple-mobile-web-app-status-bar-style: black-translucent` :
                    // iOS peint alors la barre d'état AU-DESSUS de la webview au lieu
                    // de laisser la page passer dessous. Même partage que sur Android,
                    // où l'OS peint la barre en theme_color.
                    //
                    // La note précédente (« iPhone à encoche : doit être non nul »)
                    // confondait les deux et aurait fait crier au loup sur une valeur
                    // conforme (mesuré sur iOS 26.5.2 — #72).
                    note: t('diagnostic.zoneSureNote'),
                },
            ]);
        };

        void mesurer();
        return () => {
            annule = true;
        };
    }, [standalone, permission, serviceWorker]);

    return (
        <div className="tf-shell h-full overflow-y-auto bg-main-bg-color">
            <div ref={sondeRef} className="tf-safe-probe" aria-hidden="true" />

            <main className="mx-auto flex max-w-md flex-col gap-6 px-5 py-8">
                <header className="flex flex-col gap-1">
                    <img
                        src="/pwa/icon-192.png"
                        alt=""
                        aria-hidden="true"
                        className="mb-2 h-12 w-12 rounded-xl"
                    />
                    <h1 className="text-xl font-semibold text-main-color">TriggerFlow</h1>
                    <p className="text-sm text-text-color-light">
                        {t('diagnostic.sousTitre')}
                    </p>
                </header>

                <section aria-label={t('diagnostic.etatCoque')}>
                    <dl className="flex flex-col">
                        {releves.map((releve) => (
                            <div
                                key={releve.libelle}
                                className="flex items-start justify-between gap-4 border-b border-border-color-light py-3"
                            >
                                <dt className="flex flex-col gap-1 text-sm text-text-color">
                                    {releve.libelle}
                                    {releve.note && (
                                        <span className="text-xs text-text-color-light">
                                            {releve.note}
                                        </span>
                                    )}
                                </dt>
                                <dd
                                    className={`shrink-0 text-right text-sm font-semibold ${
                                        releve.conforme === undefined
                                            ? 'text-text-color'
                                            : releve.conforme
                                              ? 'text-success-color'
                                              : 'text-alert-color'
                                    }`}
                                >
                                    {releve.valeur}
                                </dd>
                            </div>
                        ))}
                    </dl>

                    {notificationDisponible() && permission === 'default' && (
                        <Button
                            libelle={t('diagnostic.demanderPermission')}
                            onPress={demanderPermission}
                            pleineLargeur
                            className="mt-5"
                        />
                    )}

                    {/* Sur iOS le refus est définitif dans une web app installée :
                        aucune redemande n'est possible, il faut supprimer l'icône
                        et réinstaller. Le dire évite de griller un passage du test. */}
                    {permission === 'denied' && (
                        <p className="mt-3 text-xs leading-relaxed text-text-color-light">
                            {t('diagnostic.refusEnregistre')}
                        </p>
                    )}

                    {/* Informatif, sans verdict : une web app installée rend en
                        général la signature système de WebKit, sans marqueur du
                        navigateur installateur (CriOS / FxiOS / EdgiOS). Voir cette
                        signature persister est donc en soi un résultat. */}
                    <div className="mt-6 border-t border-border-color-light pt-4">
                        <p className="text-xs font-semibold text-text-color">
                            {t('diagnostic.signature')}
                        </p>
                        <p className="mt-1 break-all font-mono text-xs leading-relaxed text-text-color-light">
                            {navigator.userAgent}
                        </p>
                    </div>
                </section>
            </main>
        </div>
    );
}
